import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

export type AuthState = 'unknown' | 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface AuthSession {
  authenticated: true;
  user: { sub: string; displayName?: string; email?: string };
  sessionExpiresAt: string;
  csrfToken: string;
}

export const AUTH_NAVIGATE = new InjectionToken<(url: string) => void>('AUTH_NAVIGATE', {
  providedIn: 'root',
  factory: () => {
    const document = inject(DOCUMENT);
    return url => document.location.assign(url);
  },
});

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly navigate = inject(AUTH_NAVIGATE);
  private readonly stateValue = signal<AuthState>('unknown');
  private readonly sessionValue = signal<AuthSession | null>(null);
  private pending: Promise<AuthState> | undefined;
  private revision = 0;
  readonly state = this.stateValue.asReadonly();
  readonly session = this.sessionValue.asReadonly();
  readonly redirecting = signal(false);

  initializeAfterLogin(): void {
    const window = this.document.defaultView;
    if (!window) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('auth_return') !== '1') return;
    url.searchParams.delete('auth_return');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    void this.initialize();
  }

  initialize(force = false): Promise<AuthState> {
    if (this.pending) return this.pending;
    if (!force && (this.state() === 'anonymous' ||
      (this.state() === 'authenticated' && Date.parse(this.session()!.sessionExpiresAt) > Date.now()))) {
      return Promise.resolve(this.state());
    }
    const revision = this.revision;
    this.stateValue.set('loading');
    const request = firstValueFrom(this.http.get<AuthSession>('/auth/me')).then(response => {
      if (revision !== this.revision) return this.state();
      if (response.authenticated !== true || typeof response.user?.sub !== 'string' ||
          typeof response.csrfToken !== 'string' || response.csrfToken.length < 32 ||
          !Number.isFinite(Date.parse(response.sessionExpiresAt)) || Date.parse(response.sessionExpiresAt) <= Date.now()) {
        throw new Error('Invalid session response');
      }
      this.sessionValue.set({
        authenticated: true,
        user: { sub: response.user.sub, displayName: response.user.displayName, email: response.user.email },
        sessionExpiresAt: response.sessionExpiresAt,
        csrfToken: response.csrfToken,
      });
      this.stateValue.set('authenticated');
      return this.state();
    }).catch((error: unknown) => {
      if (revision !== this.revision) return this.state();
      this.sessionValue.set(null);
      this.stateValue.set(error instanceof HttpErrorResponse && error.status === 401 ? 'anonymous' : 'error');
      return this.state();
    }).finally(() => {
      if (this.pending === request) this.pending = undefined;
    });
    this.pending = request;
    return request;
  }

  login(returnTo = '/'): void {
    if (this.redirecting()) return;
    const destination = returnTo.startsWith('/') && !returnTo.startsWith('//') &&
      !returnTo.startsWith('/auth') && !/[\\%\s#]/.test(returnTo) ? returnTo : '/';
    this.redirecting.set(true);
    this.navigate(`/auth/login?returnTo=${encodeURIComponent(destination)}`);
  }

  async logout(): Promise<void> {
    await firstValueFrom(this.http.post<void>('/auth/logout', {}, {
      headers: this.session() ? { 'X-CSRF-Token': this.session()!.csrfToken } : {},
    }));
    this.clear();
    await this.router.navigateByUrl('/');
  }

  sessionExpired(): void {
    this.clear();
    if (!this.router.url.startsWith('/auth/error')) {
      void this.router.navigate(['/auth/error'], { queryParams: { code: 'session_expired' } });
    }
  }

  private clear(): void {
    this.revision++;
    this.sessionValue.set(null);
    this.stateValue.set('anonymous');
    this.redirecting.set(false);
  }
}